import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRoad,
  faPlus,
  faUsers,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";

interface EmptyStateProps {
  tab: "create" | "audience" | "results";
  onCreateJourney?: () => void;
}

/**
 * Empty state component to display when no journeys are available
 * Shows different content based on the active tab
 */
const EmptyState: React.FC<EmptyStateProps> = ({ tab, onCreateJourney }) => {
  // Content based on tab
  const getTabContent = () => {
    switch (tab) {
      case "create":
        return {
          icon: faRoad,
          title: "No Journey Templates Yet",
          description:
            "Create your first journey template to get started with customized team interactions.",
          buttonText: "Create Journey Template",
          action: onCreateJourney,
        };
      case "audience":
        return {
          icon: faUsers,
          title: "No Audiences Defined",
          description:
            "Define your audience to deploy your journey templates to specific channels.",
          buttonText: "Add Audience",
          action: () => {},
        };
      case "results":
        return {
          icon: faChartLine,
          title: "No Results Available",
          description:
            "Deploy a journey to channels to start collecting engagement data.",
          buttonText: "View Journeys",
          action: () => {},
        };
      default:
        return {
          icon: faRoad,
          title: "No Journeys Available",
          description: "Get started by creating your first journey.",
          buttonText: "Create Journey",
          action: onCreateJourney,
        };
    }
  };

  const content = getTabContent();

  return (
    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
      <FontAwesomeIcon
        icon={content.icon}
        className="text-gray-400 text-5xl mb-4"
      />
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        {content.title}
      </h3>
      <p className="text-gray-600 mb-6">{content.description}</p>
      {content.action && (
        <button
          onClick={content.action}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand hover:bg-brandCompliment focus:outline-none"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          {content.buttonText}
        </button>
      )}
    </div>
  );
};

export default EmptyState;
